import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiClient, getErrorMessage } from '@/App';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Eye, EyeOff, ArrowLeft } from 'lucide-react';

const MASTER_ADMIN_EMAIL = 'gmpdeveloppement@gmail.com';

const BlogAdmin = ({ user }) => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: 'guide',
    image: '',
    keywords: '',
    meta_title: '',
    meta_description: '',
    read_time: 5,
    published: false
  });

  // Vérifier si l'utilisateur est master admin
  const isMasterAdmin = user?.email?.toLowerCase() === MASTER_ADMIN_EMAIL.toLowerCase();

  useEffect(() => {
    if (!isMasterAdmin) {
      toast.error('Accès réservé à l\'administrateur');
      navigate('/');
      return;
    }
    fetchArticles();
  }, [isMasterAdmin, navigate]);

  const fetchArticles = async () => {
    try {
      const response = await apiClient.get('/blog/admin/articles');
      setArticles(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des articles');
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleTitleChange = (e) => {
    const title = e.target.value;
    setFormData({
      ...formData,
      title,
      slug: generateSlug(title)
    });
  };

  const openCreateModal = () => {
    setSelectedArticle(null);
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      category: 'guide',
      image: '',
      keywords: '',
      meta_title: '',
      meta_description: '',
      read_time: 5,
      published: false
    });
    setEditModal(true);
  };

  const openEditModal = (article) => {
    setSelectedArticle(article);
    setFormData({
      title: article.title,
      slug: article.slug,
      excerpt: article.excerpt,
      content: article.content,
      category: article.category,
      image: article.image || '',
      keywords: article.keywords?.join(', ') || '',
      meta_title: article.meta_title || '',
      meta_description: article.meta_description || '',
      read_time: article.read_time || 5,
      published: article.published
    });
    setEditModal(true);
  };

  const handleSubmit = async () => {
    if (!formData.title || !formData.slug || !formData.excerpt || !formData.content) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    const payload = {
      ...formData,
      keywords: formData.keywords.split(',').map(k => k.trim()).filter(k => k)
    };

    try {
      if (selectedArticle) {
        await apiClient.put(`/blog/admin/articles/${selectedArticle.id}`, payload);
        toast.success('Article mis à jour');
      } else {
        await apiClient.post('/blog/admin/articles', payload);
        toast.success('Article créé');
      }
      setEditModal(false);
      fetchArticles();
    } catch (error) {
      const message = getErrorMessage(error, 'Erreur');
      toast.error(message);
    }
  };

  const handleDelete = async (articleId) => {
    if (!window.confirm('Supprimer cet article ?')) return;

    try {
      await apiClient.delete(`/blog/admin/articles/${articleId}`);
      toast.success('Article supprimé');
      fetchArticles();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const togglePublished = async (article) => {
    try {
      await apiClient.put(`/blog/admin/articles/${article.id}`, {
        ...article,
        published: !article.published
      });
      toast.success(article.published ? 'Article dépublié' : 'Article publié');
      fetchArticles();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  if (!isMasterAdmin) return null;

  return (
    <div className="min-h-screen bg-slate-50 p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/blog')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au blog
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Gestion du Blog</h1>
              <p className="text-slate-500">Créez et gérez vos articles SEO</p>
            </div>
          </div>
          <Button onClick={openCreateModal} className="bg-emerald-600 hover:bg-emerald-700">
            <Plus className="w-4 h-4 mr-2" />
            Nouvel article
          </Button>
        </div>

        {/* Articles List */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase">Article</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase">Catégorie</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase">Statut</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase">Date</th>
                <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                    Chargement...
                  </td>
                </tr>
              ) : articles.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-slate-500">
                    Aucun article. Créez votre premier article !
                  </td>
                </tr>
              ) : (
                articles.map((article) => (
                  <tr key={article.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-6 py-4">
                      <div className="font-medium text-slate-900">{article.title}</div>
                      <div className="text-sm text-slate-500">/blog/{article.slug}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">
                        {article.category}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => togglePublished(article)}
                        className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium ${
                          article.published
                            ? 'bg-emerald-100 text-emerald-700'
                            : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        {article.published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                        {article.published ? 'Publié' : 'Brouillon'}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-slate-500 text-sm">
                      {new Date(article.created_at).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <Button size="sm" variant="ghost" onClick={() => openEditModal(article)}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(article.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit/Create Modal */}
      <Dialog open={editModal} onOpenChange={setEditModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedArticle ? 'Modifier l\'article' : 'Nouvel article'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Titre *</Label>
                <Input
                  value={formData.title}
                  onChange={handleTitleChange}
                  placeholder="Titre de l'article"
                />
              </div>
              <div>
                <Label>Slug (URL) *</Label>
                <Input
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                  placeholder="url-de-l-article"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Catégorie</Label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                >
                  <option value="guide">Guide</option>
                  <option value="juridique">Juridique</option>
                  <option value="conseils">Conseils</option>
                  <option value="actualites">Actualités</option>
                </select>
              </div>
              <div>
                <Label>Temps de lecture (min)</Label>
                <Input
                  type="number"
                  value={formData.read_time}
                  onChange={(e) => setFormData({ ...formData, read_time: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div>
              <Label>Image URL</Label>
              <Input
                value={formData.image}
                onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                placeholder="https://..."
              />
            </div>

            <div>
              <Label>Extrait (chapeau) *</Label>
              <textarea
                value={formData.excerpt}
                onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
                placeholder="Résumé de l'article (affiché dans la liste)"
                rows={2}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg"
              />
            </div>

            <div>
              <Label>Contenu (HTML) *</Label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                placeholder="<h2>Introduction</h2><p>Votre contenu...</p>"
                rows={12}
                className="w-full px-3 py-2 border border-slate-200 rounded-lg font-mono text-sm"
              />
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold mb-4">SEO</h3>
              <div className="space-y-4">
                <div>
                  <Label>Mots-clés (séparés par des virgules)</Label>
                  <Input
                    value={formData.keywords}
                    onChange={(e) => setFormData({ ...formData, keywords: e.target.value })}
                    placeholder="syndic bénévole, copropriété, gestion"
                  />
                </div>
                <div>
                  <Label>Meta Title</Label>
                  <Input
                    value={formData.meta_title}
                    onChange={(e) => setFormData({ ...formData, meta_title: e.target.value })}
                    placeholder="Titre pour Google (60 caractères max)"
                  />
                </div>
                <div>
                  <Label>Meta Description</Label>
                  <textarea
                    value={formData.meta_description}
                    onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                    placeholder="Description pour Google (160 caractères max)"
                    rows={2}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="published"
                checked={formData.published}
                onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="published" className="cursor-pointer">Publier immédiatement</Label>
            </div>

            <div className="flex gap-3 pt-4">
              <Button onClick={handleSubmit} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {selectedArticle ? 'Mettre à jour' : 'Créer l\'article'}
              </Button>
              <Button onClick={() => setEditModal(false)} variant="outline" className="flex-1">
                Annuler
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BlogAdmin;
